import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LinkIcon from "@mui/icons-material/Link";

import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useFetchDetail } from "@hooks/useFetchDetail";
import {
  ServicesURL,
  downloadBlobFile,
  formatSessionOccupiedError,
  onErrorMessage,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ButtonBackPage from "@components/ButtonBackPage";
import { ActionsType, ColumnsType } from "src/interfaces/Common";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { translateHeaders } from "@utils/utilsTranslateHeaders";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useFetchTable } from "@hooks/useFetchDataTable";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useErrorContext } from "@context/ErrorContext";
import dayjs from "dayjs";
import DownloadIcon from "@mui/icons-material/Download";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import { buildDateStr } from "@utils/utilsDate";
import { useFilters } from "@context/filters";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import {
  ModalEditionChange,
  EditionWithSessions,
} from "@components/ModalEditionChange";
import { Warning } from "@mui/icons-material";

const SubscribedUsers: React.FC<{
  page: PagesID;
  showIssuedCertificates?: boolean;
}> = ({ page, showIssuedCertificates }) => {
  const { t, i18n } = useTranslation([
    "customers-page",
    "toast-message",
    "modal",
  ]);
  const {
    id = "1",
    idLO,
    idEdition,
    idEditionLOActivity,
    activityType,
    idCourse,
    idPath,
  } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${idEdition ? PagesID["enrollment.subscribed.edition"] : page}`;
  const { setErrorContext, setOpenErrorContext } = useErrorContext();

  const router = useLocation();
  const navigate = useNavigate();

  const [openToastDeleted, setOpenToastDeleted] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const [enrollDeleted, setEnrollDeleted] = useState<any>();
  const [enrollGroupDeleted, setEnrollGroupDeleted] = useState<any>();
  const [enrollSingleEdit, setEnrollSingleEdit] = useState<any>();
  const [enrollGroupEdit, setEnrollGroupEdit] = useState<any>();
  const location = useLocation();
  const [groupIDWarning, setGroupIDWarning] = useState<any>();
  const [editDescriptionErrorModal, setEditDescriptionErrorModal] =
    useState(false);
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const [openModalEditionChange, setOpenModalEditionChange] = useState(false);
  const [openModalEditionChangeToast, setOpenModalEditionChangeToast] =
    useState<{
      s: StatusEnum;
      message: string;
      customContent?: JSX.Element;
    }>();
  const [selectedEditionToReschedule, setSelectedEditionToReschedule] =
    useState<any>(null);
  const [enrollableEditionsWithSessions, setEnrollableEditionsWithSessions] =
    useState<EditionWithSessions[]>([]);
  const [isPollingEditionChange, setIsPollingEditionChange] = useState(false);
  const [newEditionIdForPolling, setNewEditionIdForPolling] = useState<
    number | null
  >(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingCompletedRef = useRef<boolean>(false);

  const isOverallCatalog = useMemo(() => {
    return router.pathname.includes("overall-catalog");
  }, [router.pathname]);

  const isVirtualEditionSubscribedPage = useMemo(() => {
    return (
      page === PagesID["corporate.virtual-editions.subscribed"] ||
      router.pathname.includes("virtual-editions")
    );
  }, [page, router.pathname]);

  const getURLBasedOnParams = () => {
    if (idEdition) {
      return `${ServicesURL.getEnrollEditionDetail}?corporateId=${id}${`&editionId=${idEdition}`}${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
    } else {
      if (activityType) {
        if (isOverallCatalog) {
          return `${ServicesURL.getEnrollment}?corporateId=${id}${`&objectId=${idLO}`}${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
        } else {
          return `${ServicesURL.getEnrollment}?corporateId=${id}${`&objectId=${idPath || idCourse || idLO}`}${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
        }
      } else {
        return `${ServicesURL.getEnrollment}?corporateId=${id}${idLO ? `&objectId=${idLO}` : ``}${location?.state?.setGroup && location?.state?.row ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}` : ``}${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`;
      }
    }
  };

  const { dataTable, columns, actionForRow, actionsTopToolbar, mutate } =
    useFetchTable(
      getURLBasedOnParams(),
      idEdition ? PagesID["enrollment.subscribed.edition"] : page,
      filters[filterId],
    );

  // se nessuna riga ha un certificato disabilito il tasto download massivo
  const certificatesAvailable = useMemo(() => {
    const rows = dataTable?.enrollmentResponses ?? [];

    return rows.some(
      (el: any) =>
        (Array.isArray(el.certificateIds) && el.certificateIds.length > 0) ||
        !!el.certificateId,
    );
  }, [dataTable]);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID["corporate"],
  );

  const { trigger: triggerDelete } = useSWRMutation(
    `${ServicesURL.deleteEnrollment}`,
    (url: string, { arg }: { arg: { enrollmentEId: string } }) =>
      fetcher(`${url}?enrollmentEId=${arg.enrollmentEId}`, MethodType.DELETE),
  );

  const { trigger: triggerDownloadEnrolled } = useSWRMutation(
    `${ServicesURL.downloadEnrolledEdition}`,
    (url: string, { arg }: { arg: string }) =>
      fetcher(`${url}${arg}`, MethodType.GET),
  );

  const { trigger: triggerDeleteEdition } = useSWRMutation(
    `${ServicesURL.getEnrollEdition}`,
    (url: string, { arg }: { arg: { studentEid: string; objectId: string } }) =>
      fetcher(
        `${url}?corporateId=${id}&objectId=${arg.objectId}&studentEid=${arg.studentEid}&editionId=${idEdition}`,
        MethodType.DELETE,
      ),
  );

  const { trigger: triggerDeleteGroups } = useSWRMutation(
    `${ServicesURL.postEnrollmentMassive}`,
    (url: string, { arg }: { arg: { groupId: any; groupType: any } }) =>
      fetcher(
        `${url}?corporateId=${id}&groupId=${arg.groupId}&groupType=${arg.groupType}&objectId=${idLO}`,
        MethodType.DELETE,
      ),
  );

  const { trigger: triggerGetEnrollableEditions } = useSWRMutation(
    ServicesURL.getEnrollableEditions,
    (
      url: string,
      {
        arg,
      }: { arg: { corporateId: string; objectId: string; studentEid: string } },
    ) =>
      fetcher(
        `${url}?corporateId=${arg.corporateId}&objectId=${arg.objectId}&userId=${arg.studentEid}`,
        MethodType.GET,
      ),
    { onError: () => {} },
  );

  const { trigger: triggerGetEnrollmentStatus } = useSWRMutation(
    `${ServicesURL.getEnrollmentStatus}`,
    (
      url: string,
      {
        arg,
      }: { arg: { corporateId: string; editionId: string; userId: string } },
    ) =>
      fetcher(
        `${url}?corporateId=${arg.corporateId}&editionId=${arg.editionId}&userId=${arg.userId}`,
        MethodType.GET,
      ),
    { onError: () => {} },
  );

  const {
    trigger: triggerPatchEnrollment,
    isMutating: isMutatingPatchEnrollment,
  } = useSWRMutation(
    ServicesURL.patchEnrollment,
    (url: string, { arg }: { arg: any[] }) =>
      fetcher(url, MethodType.PATCH, arg),
    { onError: () => {} },
  );

  const pollEnrollmentStatus = useCallback(async () => {
    if (!selectedEditionToReschedule || !newEditionIdForPolling) return;

    try {
      const response: any = await triggerGetEnrollmentStatus({
        corporateId: String(id),
        editionId: String(newEditionIdForPolling),
        userId: String(selectedEditionToReschedule.idUser),
      });

      const currentStatus = response?.status || response?.data?.status;
      const currentErrorMessage =
        response?.errorMessage || response?.data?.errorMessage || "";

      if (pollingCompletedRef.current) return;

      if (currentStatus === "COMPLETE") {
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        setOpenModalEditionChangeToast({
          s: StatusEnum.OK,
          message: t("change-edition.edition_change_success", { ns: "modal" }),
        });
        mutate();
      } else if (currentStatus === "ERROR") {
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        if (currentErrorMessage.includes("MAX_EDITION_CAPACITY_REACHED")) {
          setOpenModalEditionChangeToast({
            s: StatusEnum.KO,
            message: t("max-capacity-reached-error", { ns: "modal" }),
          });
        } else {
          setOpenModalEditionChangeToast({
            s: StatusEnum.KO,
            message: t("change-edition.enrollment_edition_error", {
              ns: "modal",
            }),
          });
        }
        mutate();
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
  }, [
    id,
    newEditionIdForPolling,
    selectedEditionToReschedule,
    triggerGetEnrollmentStatus,
    mutate,
    t,
  ]);

  useEffect(() => {
    if (!isPollingEditionChange) {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      return;
    }

    const startTime = Date.now();
    const MAX_POLLING_DURATION = 120000; // 2 minutes
    const MAX_INTERVAL = 20000; // 20 seconds
    let currentInterval = 5000; // Start with 5 seconds

    const scheduleNextPoll = () => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= MAX_POLLING_DURATION) {
        pollingCompletedRef.current = true;
        setIsPollingEditionChange(false);
        setOpenModalEditionChangeToast({
          s: StatusEnum.KO,
          message: t("change-edition.enrollment_polling_error", {
            ns: "modal",
          }),
        });
        mutate();
        return;
      }

      pollEnrollmentStatus();

      if (currentInterval < MAX_INTERVAL) {
        currentInterval += 5000;
      }

      pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);
    };

    pollingTimeoutRef.current = setTimeout(scheduleNextPoll, currentInterval);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, [isPollingEditionChange, pollEnrollmentStatus, mutate, t]);

  //:TODO  Da eliminare
  /*
  useEffect(() => {
    if (location?.state?.row && location?.state?.setGroup) {
      setFilters &&
        setFilters(`${user?.id_user}_${page}`, {
          massiveGroupId: location?.state?.row[location?.state?.setGroup],
        });
    }
  }, []);
  */

  const { trigger: triggerIsMultiScorm } = useSWRMutation(
    ServicesURL.scorm + `/is-multiscorm`,
    (url: string, { arg }: { arg: any }) => {
      return fetcher<any>(
        `${url}?corporateId=${id}&studentId=${arg?.userId}&activityId=${idLO}&learningObjectId=${arg?.learningObject?.idLearningObject}&parentActivityId=${idCourse || idPath || arg?.learningObject?.parentId || ""}&grandparentActivityId=${idPath && idCourse ? idPath : arg?.learningObject?.grandParentId || ""}`,
        MethodType.GET,
      );
    },
    { onError: () => null },
  );

  const isMultiscorm = async (row: any, index: any) => {
    let result = await triggerIsMultiScorm(row);
    if (result) {
      navigate(
        `${row?.userId}/${idLO}/${row?.learningObject?.idLearningObject}/${idCourse || idPath || row?.learningObject?.parentId}/${idPath && idCourse ? idPath : row?.learningObject?.grandParentId}/multi-scorm`,
      );
    } else {
      navigate(
        `${row?.userId}/${idLO}/${row?.learningObject?.idLearningObject}/${idCourse || idPath || row?.learningObject?.parentId}/${idPath && idCourse ? idPath : row?.learningObject?.grandParentId}/change-status`,
      );
    }
  };

  const configurationActionForRow: ActionsType[] | undefined =
    React.useMemo(() => {
      let actionsArray = actionForRow;
      const isSon =
        router.pathname.includes("overall-catalog") &&
        router.pathname.includes("associated-");

      if (isSon) {
        const hideIconsKey = [
          "single_enroll",
          "delete_single_enroll",
          "group_enroll",
        ];
        const hideIconsList = actionForRow?.filter((action: any) => {
          return !hideIconsKey.includes(action.key);
        });
        actionsArray = hideIconsList;
      }

      return actionsArray?.map((action: any) => {
        if (action.key === "reschedule") {
          return {
            ...action,
            callBack: async (row: any) => {
              try {
                const corporateId = id || "1";
                const objectId = row.learningObject?.objectId ?? row.objectId;
                const studentEid = row.idUser;

                const response = await triggerGetEnrollableEditions({
                  corporateId: String(corporateId),
                  objectId: String(objectId),
                  studentEid: String(studentEid),
                });

                if (response?.editions && response?.editions?.length > 0) {
                  setSelectedEditionToReschedule(row);
                  setEnrollableEditionsWithSessions(response?.editions);
                  setOpenModalEditionChange(true);
                } else {
                  setOpenModalEditionChangeToast({
                    s: StatusEnum.CUSTOM,
                    message: "",
                    customContent: (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          gap: "1rem",
                          mb: "1.5rem",
                        }}
                      >
                        <Warning sx={{ fontSize: "60px", color: "orange" }} />
                        <Typography variant="h5" component={"p"}>
                          {t("change-edition.no_edition_available_for_change", {
                            ns: "modal",
                          })}
                        </Typography>
                      </Box>
                    ),
                  });
                }
              } catch (error) {
                const message = onErrorMessage(error);
                setOpenModalEditionChangeToast({ s: StatusEnum.KO, message });
                console.error(error);
              }
            },
          };
        }
        if (action.key === "change-status") {
          return {
            ...action,
            callBack: (row: any, index: any) => {
              if (activityType === "DA_SCORM") {
                isMultiscorm(row, index);
              } else {
                navigate(
                  `${row?.userId}/${idLO}/${row?.learningObject?.idLearningObject}/${idCourse || idPath || row?.learningObject?.parentId}/${idPath && idCourse ? idPath : row?.learningObject?.grandParentId}/change-status`,
                );
              }
            },
          };
        }
        if (action.type === "button" && action.icon === "download") {
          return {
            ...action,
            callBack: async (row: any) => {
              const learnerId = row.userId;
              const objectId = row.learningObject?.objectId ?? row.objectId;

              const certificateIds: number[] = Array.isArray(row.certificateIds)
                ? row.certificateIds
                : [];

              if (!objectId || !learnerId || certificateIds.length === 0)
                return;

              try {
                setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

                if (certificateIds.length === 1) {
                  const issuedCertificateId = certificateIds[0];

                  const fileName = `issued_certificate_lo_${objectId}_learner_${learnerId}.pdf`;

                  const url = `${ServicesURL.getIssuedCertificate.replace(":certificateId", String(issuedCertificateId))}?objectId=${objectId}&learnerId=${learnerId}`;

                  await downloadBlobFile(url, null, fileName, MethodType.GET);
                } else {
                  const issuedCertificates = certificateIds.map(
                    (issuedCertificateId) => ({
                      learnerId,
                      objectId,
                      issuedCertificateId,
                    }),
                  );

                  const fileName = `issued_certificates_lo_${objectId}_learner_${learnerId}.zip`;

                  await downloadBlobFile(
                    `${ServicesURL.getIssuedCertificatesZip}`,
                    {
                      issuedCertificates: issuedCertificates,
                    },
                    fileName,
                  );
                }

                setOpenToast({ s: StatusEnum.OK, message: "" });
              } catch (error) {
                const message = onErrorMessage(error);
                setOpenToast({ s: StatusEnum.KO, message: message });
                console.error(error);
              }
            },
            getIcon: (row: any) => <DownloadIcon />,
          };
        } else {
          if (action.type === "button" && action.icon !== "delete") {
            if (idEdition) return {};
            return {
              ...action,
              callBack: (row: any) => {
                if (
                  row?.learningObject?.blendedParentId &&
                  row?.learningObject?.blendedParentId !== Number(idLO) &&
                  action.key !== "enroll_history"
                ) {
                  setEditDescriptionErrorModal(true);
                  setTitleError("Modifica Iscrizione");
                  setDescriptionError(
                    `L'utente è stato iscritto all'oggetto formativo tramite Percorso/Corso ID "${row.learningObject.blendedParentId}". Per poter modificare l'iscrizione dell'utente all'oggetto formativo, è necessario modificare l'iscrizione sul Percorso/Corso ID "${row.learningObject.blendedParentId}"`,
                  );
                  return;
                }

                const isAfterStartEdition = dayjs().isAfter(
                  dayjs(row["editionStartDate"]),
                );
                const isBeforeEndEdition = dayjs().isBefore(
                  dayjs(row["editionEndDate"]),
                );

                const isEditionInProgress =
                  isAfterStartEdition && isBeforeEndEdition;

                if (
                  !!row?.editionStartDate &&
                  isEditionInProgress &&
                  action.key !== "enroll_history"
                ) {
                  setErrorContext({
                    title: "Warning",
                    content: `${t("warning-startEdition-enroll", {
                      ns: "modal",
                    })}`,
                  });
                  setOpenErrorContext(true);
                  return;
                }

                if (action.key === "group_enroll") {
                  setEnrollGroupEdit(row);
                  setGroupIDWarning(row?.massiveGroupId);
                } else if (action.key === "single_enroll") {
                  navigate(`${row.userId}/enrollment`);
                  // if (row?.blended) {
                  //   // setEnrollSingleEdit(row)
                  //   setErrorContext({
                  //     title: "Warning",
                  //     content: `${t("warning-blended-enroll", {
                  //       ns: "modal",
                  //     })}`,
                  //   });
                  //   setOpenErrorContext(true);
                  // } else {
                  //   navigate(`${row.userId}/enrollment`);
                  // }
                } else if (action.key === "enroll_history") {
                  navigate(`${row.userId}/enrollment-history`);
                }
              },
            };
          }
          if (action.type === "button" && action.icon === "delete") {
            if (
              action.key === "single_enroll" ||
              action.key === "delete_single_enroll"
            ) {
              return {
                ...action,
                callBack: (row: any) => {
                  if (
                    !!row?.learningObject?.blendedParentId &&
                    row?.learningObject?.blendedParentId !== Number(idLO)
                  ) {
                    setEditDescriptionErrorModal(true);
                    setTitleError("Elimina Iscrizione");
                    setDescriptionError(
                      `L'utente è stato iscritto all'oggetto formativo tramite Percorso/Corso ID "${row.learningObject.blendedParentId}". Per poter eliminare l'iscrizione dell'utente all'oggetto formativo, è necessario eliminare l'iscrizione sul Percorso/Corso ID "${row.learningObject.blendedParentId}"`,
                    );
                    return;
                  }
                  setEnrollDeleted(row);
                  setGroupIDWarning(row?.massiveGroupId);
                  // if (row?.blended) {
                  //   setErrorContext({
                  //     title: "Warning",
                  //     content: `${t("warning-blended-enroll", {
                  //       ns: "modal",
                  //     })}`,
                  //   });
                  //   setOpenErrorContext(true);
                  // } else {
                  //   setEnrollDeleted(row);
                  //   setGroupIDWarning(row?.massiveGroupId);
                  // }
                },
                labelTranslate: (action.label as any)[i18n.language],
              };
            } else {
              if (idEdition) return {};
              return {
                ...action,
                callBack: (row: any) => {
                  if (
                    !!row?.learningObject?.blendedParentId &&
                    row?.learningObject?.blendedParentId !== Number(idLO)
                  ) {
                    setEditDescriptionErrorModal(true);
                    setTitleError("Elimina Iscrizione");
                    setDescriptionError(
                      `L'utente è stato iscritto all'oggetto formativo tramite Percorso/Corso ID "${row.learningObject.blendedParentId}". Per poter eliminare l'iscrizione dell'utente all'oggetto formativo, è necessario eliminare l'iscrizione sul Percorso/Corso ID "${row.learningObject.blendedParentId}"`,
                    );
                    return;
                  }
                  setEnrollGroupDeleted(row);
                  setGroupIDWarning(row?.massiveGroupId);
                },
                labelTranslate: (action.label as any)[i18n.language],
              };
            }
          }
        }
        return {
          ...action,
          labelTranslate: (action.label as any)[i18n.language],
        };
      });
    }, [actionForRow, i18n.language]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    configRowNew.forEach((configRow: any) => {
      let fixedDisable = false;
      if (!idEdition && configRow.key === "group_enroll" && !fixedDisable) {
        if (row.original?.massiveGroupId) {
          configRow.disabled = false;
        } else {
          configRow.disabled = true;
          fixedDisable = true;
        }
      }

      if (
        configRow.key === "reschedule" ||
        configRow.key === "delete_single_enroll" ||
        (configRow.key === "single_enroll" && configRow.icon === "delete")
      ) {
        const hasUserCompletedEdition = row?.original?.status === "C";
        configRow.disabled = hasUserCompletedEdition;
      }

      if (configRow.key === "download_certificate") {
        const hasDownloadCertificatePermission = user?.permissions.find(
          (userPermission) => userPermission === configRow.permission,
        );

        // configRow.disabled =
        //   !row.original?.certificateId || !hasDownloadCertificatePermission;
        const ids = row.original?.certificateIds;
        configRow.disabled =
          !Array.isArray(ids) ||
          ids.length === 0 ||
          !hasDownloadCertificatePermission;
      }

      if (configRow.key === "change-status") {
        let activitiesCategories = [
          "DA_XAPI",
          "DA_SCORM",
          "DA_EXTERNAL_RES",
          "DA_YOUTUBE",
          "DA_LINKEDIN",
          //"DA_LTI",
          "DA_SURVEY",
          "DA_URL_RES",
        ];

        if (
          activitiesCategories.includes(`${activityType}`) &&
          row.original?.status === "E"
        ) {
          configRow.disabled = true;
        } else {
          configRow.disabled = false;
        }
      }
    });

    let activitiesCategories = [
      "DA_XAPI",
      "DA_SCORM",
      "DA_PHYSICAL_CLASS",
      "DA_VIRTUAL_CLASS",
      "DA_EXTERNAL_RES",
      "DA_YOUTUBE",
      "DA_LINKEDIN",
      //"DA_LTI",
      "DA_SURVEY",
      "DA_URL_RES",
    ];

    if (!activitiesCategories.includes(`${activityType}`)) {
      configRowNew = configRowNew.filter(
        (configRow: any) => configRow.key !== "change-status",
      );
    }

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const configurationTopToolbar: ActionsType[] | undefined =
    React.useMemo(() => {
      return (actionsTopToolbar || []).map((action: any) => {
        if (action.type === "button" && action.icon === "download") {
          const hasDownloadCertificatePermission = user?.permissions.find(
            (userPermission) => userPermission === action.permission,
          );
          if (!hasDownloadCertificatePermission) {
            return {};
          }
          return {
            ...action,
            labelTranslate: (action.label as any)[i18n.language],
            callBack: certificatesAvailable
              ? async (el: any) => {
                  try {
                    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

                    const fileName = `certificates_${buildDateStr(new Date())}.zip`;

                    const { enrollmentResponses } = dataTable;
                    const issuedCertificates = (
                      enrollmentResponses ?? []
                    ).flatMap((row: any) => {
                      const learnerId = row.userId;
                      const objectId =
                        row.learningObject?.objectId ?? row.objectId;

                      const certificateIds: number[] =
                        Array.isArray(row.certificateIds) &&
                        row.certificateIds.length > 0
                          ? row.certificateIds
                          : row.certificateId
                            ? [row.certificateId]
                            : [];

                      if (
                        !learnerId ||
                        !objectId ||
                        certificateIds.length === 0
                      )
                        return [];

                      return certificateIds.map((issuedCertificateId) => ({
                        learnerId,
                        objectId,
                        issuedCertificateId,
                      }));
                    });

                    if (issuedCertificates.length === 0) {
                      setOpenToast({
                        s: StatusEnum.KO,
                        message:
                          "Nessun certificato disponibile per il download",
                      });
                      return;
                    }

                    await downloadBlobFile(
                      `${ServicesURL.getIssuedCertificatesZip}`,
                      {
                        issuedCertificates: issuedCertificates,
                      },
                      fileName,
                    );
                    setOpenToast({ s: StatusEnum.OK, message: "" });
                  } catch (error) {
                    const message = onErrorMessage(error);
                    setOpenToast({ s: StatusEnum.KO, message: message });
                    console.error(error);
                  }
                }
              : null,
            getIcon: () => <DownloadIcon />,
          };
        }
        return {
          ...action,
          labelTranslate: (action.label as any)[i18n.language],
        };
      });
    }, [actionsTopToolbar, i18n.language]);

  const handleDelete = React.useCallback(async () => {
    if (enrollDeleted) {
      setOpenToastDeleted({ s: StatusEnum.LOADING, message: "loading" });
      let newData = [];
      try {
        if (idEdition) {
          await triggerDeleteEdition({
            studentEid: enrollDeleted.idUser.toString(),
            objectId: enrollDeleted.objectId.toString(),
          });
          newData = [
            ...dataTable.users.filter(
              (el: any) => el.idUser !== enrollDeleted.idUser,
            ),
          ];
        } else {
          await triggerDelete({
            enrollmentEId: enrollDeleted.enrollId.toString(),
          });
          newData = [
            ...dataTable.enrollmentResponses.filter(
              (el: any) => el.id !== enrollDeleted.enrollId,
            ),
          ];
        }

        mutate && mutate({ ...dataTable, ...{ enrollmentResponses: newData } });
        setOpenToastDeleted({ s: StatusEnum.OK, message: "ok" });
      } catch (e) {
        console.error(e);
        setEnrollDeleted(undefined);
        setOpenToastDeleted(undefined);
      }
    }
  }, [dataTable, enrollDeleted, mutate, triggerDelete]);

  const handleDeleteGroup = React.useCallback(async () => {
    if (enrollGroupDeleted) {
      setOpenToastDeleted({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteGroups({
          groupId: enrollGroupDeleted?.massiveGroupId,
          groupType: enrollGroupDeleted?.groupType,
        });
        const newData = [
          ...dataTable.enrollmentResponses.filter(
            (el: any) =>
              el.massiveGroupId !== enrollGroupDeleted.massiveGroupId,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ enrollmentResponses: newData } });
        setOpenToastDeleted({ s: StatusEnum.OK, message: "ok" });
      } catch (e) {
        console.error(e);
        setOpenToastDeleted(undefined);
        setEnrollGroupDeleted(undefined);
      }
    }
  }, [dataTable, enrollGroupDeleted, mutate, triggerDelete]);

  const getEnrollmentResponse = () => {
    const newList = dataTable?.enrollmentResponses.map((item: any) => {
      let newItem = {
        ...item,
        grandParentId: item.learningObject.grandParentId,
        parentId: item.learningObject.parentId,
      };
      if (
        !!item.learningObject.blendedParentId &&
        item.learningObject.blendedParentId !== Number(idLO)
      ) {
        return {
          ...newItem,
          blendedParentId: item.learningObject.blendedParentId,
        };
      }
      return { ...newItem };
    });

    return newList || [];
  };
  const downloadEnrolled = async () => {
    let filteredQueryParams = "";
    if (!filters[filterId]) {
      filteredQueryParams = `&pageIndex=0&pageSize=20`;
    } else {
      for (const key in filters[filterId]) {
        filteredQueryParams =
          filteredQueryParams + `&${key}=${filters[filterId][key]}`;
      }
    }

    try {
      let response = await triggerDownloadEnrolled(
        `?editionId=${idEdition}&corporateId=${id || "1"}${filteredQueryParams}`,
      );
      let fileName = `users_subscribed_page n^${(filters[filterId] ? filters[filterId].pageIndex : 0) + 1}.csv`;
      const blob = new Blob([response], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
    }
  };

  const [columnsMerged, setColumnsMerged] = useState<ColumnsType[]>();

  useEffect(() => {
    if (!columnsMerged) {
      if (columns) {
        setColumnsMerged([
          ...(columns.map((el: ColumnsType) => {
            switch (el.accessorKey) {
              case "massiveGroupId": {
                return {
                  ...el,
                  hasFilter:
                    location && location?.state && location?.state?.setGroup
                      ? false
                      : true,
                };
              }
              default:
                return el;
            }
          }) || []),
        ]);
      }
    }
  }, [columns, columnsMerged]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-users.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${location.state?.row?.title || dataDetail?.name} - ${t(
            "subscribed-users.hero-title",
          )}`}
          description={t("subscribed-users.hero-description")}
        />
        <ButtonBackPage customRedirect={-1} />
        {columns ? (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={idEdition ? PagesID["enrollment.subscribed.edition"] : page}
              data={
                idEdition
                  ? dataTable?.users || []
                  : getEnrollmentResponse() || []
              }
              columns={
                columnsMerged
                  ? translateHeaders(columnsMerged, i18n.language)
                  : []
              }
              configurationTopToolbar={configurationTopToolbar.map(
                (action: any) => {
                  if (action.accessorKey === "download_enrolled") {
                    // TODO: Rimuovere questo workaround quando i coni di visibilità saranno fixati
                    const isPrivilegedRole =
                      user?.roles?.includes(RolesDigitedEnum.PRODUCT_OWNER) ||
                      user?.roles?.includes(RolesDigitedEnum.BUSINESS_ADMIN);
                    const isDownloadDisabledForHR =
                      process.env.REACT_APP_DISABLE_DOWNLOAD_USERS_HR ===
                        "true" &&
                      user?.roles?.includes(RolesDigitedEnum.HR) &&
                      !isPrivilegedRole;

                    return {
                      ...action,
                      callBack:
                        !isDownloadDisabledForHR &&
                        idEdition &&
                        dataTable?.users &&
                        dataTable?.users.length > 0
                          ? () => downloadEnrolled()
                          : null,
                    };
                  } else {
                    return action;
                  }
                },
              )}
              configurationActionForRow={configurationActionForRow}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.totalElements}
              modalTitle={t("hero-title")?.replace("-", "") || ""}
            />
          </Box>
        ) : (
          <FormLoadingStateCst />
        )}
      </Container>
      <ModalConfirmsCst
        open={!!enrollGroupEdit}
        title={t("edit-item", { ns: "modal" })}
        description={
          groupIDWarning && !idEdition
            ? t("warning-edit-group-enroll", { ns: "modal" }).replace(
                ":ID",
                groupIDWarning,
              )
            : t("confirm-edit", { ns: "modal" })
        }
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEnrollGroupEdit(undefined);
          setOpenToastDeleted(undefined);
        }}
        onActionConfirmed={() =>
          enrollGroupEdit &&
          navigate(
            `${enrollGroupEdit.groupType}/${enrollGroupEdit.massiveGroupId}/enrollment`,
          )
        }
        status={openToastDeleted}
      />
      <ModalConfirmsCst
        open={!!enrollDeleted}
        title={t("delete-item", { ns: "modal" })}
        description={
          groupIDWarning && !idEdition
            ? t("warning-delete-single-enroll", { ns: "modal" }).replace(
                ":ID",
                groupIDWarning,
              )
            : t("confirm-delete", { ns: "modal" })
        }
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEnrollDeleted(undefined);
          setOpenToastDeleted(undefined);
        }}
        onActionConfirmed={() => enrollDeleted && handleDelete()}
        status={openToastDeleted}
      />
      <ModalConfirmsCst
        open={!!enrollGroupDeleted}
        title={t("delete-item", { ns: "modal" })}
        description={
          groupIDWarning && !idEdition
            ? t("warning-delete-group-enroll", { ns: "modal" }).replace(
                ":ID",
                groupIDWarning,
              )
            : t("confirm-delete", { ns: "modal" })
        }
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEnrollGroupDeleted(undefined);
          setOpenToastDeleted(undefined);
        }}
        onActionConfirmed={() => enrollGroupDeleted && handleDeleteGroup()}
        status={openToastDeleted}
      />
      <ModalConfirmsCst
        open={editDescriptionErrorModal}
        title={titleError}
        description={descriptionError}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setEditDescriptionErrorModal(false);
          setTitleError("");
          setDescriptionError("");
        }}
        removeButtons={true}
      />
      <ModalConfirmsCst
        open={!!openToast}
        title={t("download-certificates.title", { ns: "modal" })}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        removeButtons={true}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openModalEditionChangeToast}
        title={""}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalEditionChangeToast(undefined);
          if (
            !isPollingEditionChange &&
            enrollableEditionsWithSessions.length !== 0
          )
            navigate(-1);
        }}
        status={openModalEditionChangeToast}
      />
      {openModalEditionChange && enrollableEditionsWithSessions ? (
        <ModalEditionChange
          open={openModalEditionChange}
          handleClose={() => {
            setOpenModalEditionChange(false);
            setEnrollableEditionsWithSessions([]);
          }}
          enrollableEditionsWithSessions={enrollableEditionsWithSessions}
          onEditionChange={async (newEditionId) => {
            if (!selectedEditionToReschedule) return;
            try {
              const objectId =
                selectedEditionToReschedule.learningObject?.objectId ??
                selectedEditionToReschedule.objectId;

              await triggerPatchEnrollment([
                {
                  corporateId: id ? +id : undefined,
                  editionId: newEditionId,
                  objectId: objectId ? +objectId : undefined,
                  isToj: false,
                  userId: [selectedEditionToReschedule.idUser],
                  mandatory: null,
                  isAdmin: true,
                },
              ]);

              setOpenModalEditionChange(false);
              setEnrollableEditionsWithSessions([]);

              setOpenModalEditionChangeToast({
                s: StatusEnum.OK,
                message: t("change-edition.enrollment_edition_success", {
                  ns: "modal",
                }),
              });

              setNewEditionIdForPolling(newEditionId);
              pollingCompletedRef.current = false;
              setIsPollingEditionChange(true);
            } catch (error: any) {
              setOpenModalEditionChange(false);
              setEnrollableEditionsWithSessions([]);

              setOpenModalEditionChangeToast({
                s: StatusEnum.KO,
                message:
                  formatSessionOccupiedError(error, t) ||
                  t("change-edition.enrollment_edition_error", { ns: "modal" }),
              });
              mutate();
            }
          }}
          isVirtual={isVirtualEditionSubscribedPage}
          isLoading={isMutatingPatchEnrollment}
          isPollingEditionChange={isPollingEditionChange}
        />
      ) : null}
    </AnimationFadeIn>
  );
};

export default SubscribedUsers;
